/**
 * Cookie-Einwilligung – eine Quelle für Banner, Analytics und Server.
 *
 * Gespeichert wird im Cookie `cookie-consent` (damit der Server weiß, ob
 * der Banner im HTML offen gerendert wird) und zusätzlich in localStorage
 * (Abwärtskompatibilität). Das Consent-Cookie ist technisch notwendig
 * (§ 25 Abs. 2 TDDDG) und braucht selbst keine Einwilligung.
 */
export const CONSENT_COOKIE = "cookie-consent";
export const CONSENT_KEY = "cookie-consent";
/** Bei strukturellen Änderungen am Banner erhöhen → Besucher sehen ihn einmal neu. */
export const CONSENT_VERSION = 2;

export type StoredConsent = {
  v: number;
  necessary: true;
  statistics: boolean;
  marketing: boolean;
  ts: number;
};

export function parseConsent(raw: string | null | undefined): StoredConsent | null {
  if (!raw) return null;
  try {
    let text = raw;
    try {
      text = decodeURIComponent(raw);
    } catch {
      /* unkodiert gespeichert */
    }
    const parsed = JSON.parse(text) as Partial<StoredConsent>;
    if (parsed?.v !== CONSENT_VERSION) return null;
    return {
      v: CONSENT_VERSION,
      necessary: true,
      statistics: !!parsed.statistics,
      marketing: !!parsed.marketing,
      ts: typeof parsed.ts === "number" ? parsed.ts : Date.now(),
    };
  } catch {
    return null;
  }
}

/** Nur im Browser: Einwilligung in Cookie + localStorage schreiben und Event feuern. */
export function storeConsent(consent: StoredConsent): void {
  if (typeof window === "undefined") return;
  const json = JSON.stringify(consent);
  try {
    window.localStorage?.setItem?.(CONSENT_KEY, json);
  } catch {
    /* privater Modus */
  }
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${CONSENT_COOKIE}=${encodeURIComponent(json)}; path=/; max-age=31536000; SameSite=Lax${secure}`;
  window.dispatchEvent(new CustomEvent<StoredConsent>("cookie-consent:changed", { detail: consent }));
}

/** Nur im Browser: gespeicherte Einwilligung lesen (Cookie, sonst localStorage). */
export function readStoredConsent(): StoredConsent | null {
  if (typeof window === "undefined") return null;
  const fromCookie = document.cookie
    .split("; ")
    .find((c) => c.startsWith(`${CONSENT_COOKIE}=`))
    ?.slice(CONSENT_COOKIE.length + 1);
  const viaCookie = parseConsent(fromCookie);
  if (viaCookie) return viaCookie;
  try {
    return parseConsent(window.localStorage?.getItem?.(CONSENT_KEY));
  } catch {
    return null;
  }
}
